// ─────────────────────────────────────────────────────────────────────────────
// matcher.ts — Single-stroke spell matcher
//
// At stroke release, the engine asks each registered matcher in turn
// "does this shape (and the page state) call your spell?". Matchers are
// pure — they read shape + context, never dispatch. The first matcher
// returning a non-null SpellMatch wins.
//
// Priority is by registration order; high-specificity matchers register
// first. Once we grow a shape history, matchers will take a chain
// instead of a single shape — same return shape, more inputs.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokeShape } from './detection/shape_detector';
import type { WvSelection } from '../notebook_state';
import type { PageId, Point, RegionBounds } from '@shared/notebook_model';

/** Geometry queries the matcher can run against current page state.
 *  Closures owned by the caller (CanvasApp) — recipes never see these,
 *  only the resolved ids the matcher attached to the SpellMatch. */
export interface MatchContext {
  pageId: PageId | null;
  selection: WvSelection;
  /** Block whose bbox contains the point, or null. */
  blockAtPoint(p: Point): string | null;
  /** Block ids whose bbox lies within the given region. */
  blocksInRegion(region: RegionBounds): string[];
  /** Stroke ids whose bbox lies within the given region. */
  strokesInRegion(region: RegionBounds): string[];
  /** Stroke ids intersected by the given line segment. */
  strokesCrossingLine(from: Point, to: Point): string[];
}

export interface SpellMatch<P = Record<string, unknown>> {
  spellId: string;
  params: P;
}

export type ShapeMatcher<P = Record<string, unknown>> = (
  shape: StrokeShape,
  ctx: MatchContext,
) => SpellMatch<P> | null;

/** Ordered list of matchers. The first non-null wins. */
export class MatcherChain {
  private readonly _matchers: Array<{ spellId: string; matcher: ShapeMatcher }> = [];

  register(spellId: string, matcher: ShapeMatcher): void {
    this._matchers.push({ spellId, matcher });
  }

  match(shape: StrokeShape, ctx: MatchContext): SpellMatch | null {
    for (const { matcher } of this._matchers) {
      const m = matcher(shape, ctx);
      if (m) return m;
    }
    return null;
  }

  /** For debug surfaces. */
  list(): string[] { return this._matchers.map(m => m.spellId); }
}
